import { useEffect, useState } from "react";
import { api, type AuditEntry } from "../api.js";

/**
 * The audit viewer — BUILD_PLAN 1.19, NFR-30, AC-11, FR-ADMIN-7.
 *
 * What it shows is decided server-side from who is asking (§4.2: own, session,
 * or all). This screen renders whatever comes back and never asks for a wider
 * scope, because a viewer that could would be the way to leak the club's whole
 * history.
 *
 * Entries arrive already in words — "ORGANISER committed the session" rather
 * than `session.commit`. The before/after state is behind a tap: AC-11
 * requires it to be recorded and available, not that it fills the screen.
 */
export function AuditLog({
  sessionId,
  onClose,
}: {
  sessionId?: string;
  onClose: () => void;
}): React.JSX.Element {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    api
      .audit({ sessionId, limit: 200 })
      .then((r) => setEntries(r.entries))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load the history."),
      );
  }, [sessionId]);

  return (
    <main className="session">
      <header className="session-head">
        <strong>History</strong>
        <button className="link" onClick={onClose}>
          Back
        </button>
      </header>

      {error ? <p className="error">{error}</p> : null}
      {!entries ? <p className="muted small">Loading…</p> : null}

      {entries && entries.length === 0 ? (
        <p className="muted small">
          Nothing recorded yet that you can see. Members see their own actions;
          admins see everything.
        </p>
      ) : null}

      <ul className="queue">
        {(entries ?? []).map((entry) => (
          <li key={entry.id} className="audit-entry">
            <div className="stack" style={{ gap: ".25rem", width: "100%" }}>
              <div className="row space-between">
                <span>{entry.summary}</span>
                <span className="muted small">
                  {new Date(entry.at).toLocaleString([], {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>

              {entry.reason ? <span className="muted small">{entry.reason}</span> : null}

              {/* AC-11 — before and after are recorded and reachable. Behind a
                  tap because a wall of JSON is not a history anybody reads. */}
              {entry.before || entry.after ? (
                <button
                  className="link"
                  style={{ alignSelf: "flex-start", padding: ".2rem 0" }}
                  onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                >
                  {expanded === entry.id ? "Hide what changed" : "What changed"}
                </button>
              ) : null}

              {expanded === entry.id ? (
                <pre className="audit-detail">
                  {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
                </pre>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
